import { useState } from 'react';

import Modal from '@lib/core/src/components/Modal';
import ToastVariant from '@lib/core/src/enums/ToastVariant';
import palette from '@lib/core/src/theme/palette';
import CloseIcon from '@mui/icons-material/Close';
import { Stack, Typography } from '@mui/material';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import { EVENTS } from '@/analytics/consts';
import { useAnalytics } from '@/analytics/useAnalytics';
import { useLaunchMeetingBot } from '@/api/generated';
import { launchErrorToast } from '@/utils/toast';

import {
  ModalContainer,
  ModalDescription,
  ModalTitle,
  ModalActions,
  StyledSubmitButton,
  StyledCancelButton,
  StyledInputLabel,
  StyledTextFieldWrapper,
  StyledTextFieldBase
} from './InviteJourneyAiModalStyle';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function InviteJourneyModal({ open, onClose }: Props) {
  const { captureEvent } = useAnalytics();
  const [meetingUrl, setMeetingUrl] = useState('');
  const { mutate: launchMeetingBot, isPending } = useLaunchMeetingBot();
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!meetingUrl) return;

    launchMeetingBot(
      { data: { meetingUrl } },
      {
        onSuccess: () => {
          captureEvent(EVENTS.MEETING_BOT_INVITED_ON_DEMAND, {
            meetingUrl: meetingUrl
          });
          setMeetingUrl('');
          enqueueSnackbar({
            variant: 'custom',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            customProps: {
              sx: {
                borderRadius: '48px',
                backgroundColor: palette.black
              },
              caption: (
                <Typography color={palette.white} fontSize="14px" fontWeight={600}>
                  Journey AI bot will shortly join the meeting.
                </Typography>
              ),
              variant: ToastVariant.INFO,
              cta: {
                label: <CloseIcon sx={{ color: palette.white, width: '14px', height: '14px' }} />,
                onClick: () => {
                  closeSnackbar();
                }
              }
            }
          });
          onClose();
        },
        onError: () => {
          launchErrorToast('Error launching bot');
        }
      }
    );
  };

  return (
    <Modal sx={{ width: '95vw', maxWidth: '604px' }} open={open} onClose={onClose}>
      <ModalContainer spacing={2}>
        <form onSubmit={handleSubmit}>
          <Stack>
            <ModalTitle>Add JourneyAI to meeting</ModalTitle>
            <ModalDescription>
              JourneyAI will record, transcript, and send a summary at the end of your meeting
            </ModalDescription>
          </Stack>

          <StyledTextFieldWrapper>
            <StyledInputLabel>Meeting link</StyledInputLabel>
            <StyledTextFieldBase
              placeholder="Paste meeting link here..."
              value={meetingUrl}
              onChange={e => setMeetingUrl(e.target.value)}
              fullWidth
            />
          </StyledTextFieldWrapper>

          <ModalActions direction="row" justifyContent="flex-end" spacing={1}>
            <StyledCancelButton onClick={onClose}>Cancel</StyledCancelButton>
            <StyledSubmitButton
              variant="contained"
              type="submit"
              disabled={!meetingUrl || isPending}
            >
              Submit
            </StyledSubmitButton>
          </ModalActions>
        </form>
      </ModalContainer>
    </Modal>
  );
}
